import React from 'react'
import { Mail, Plus, Trash2, CheckCircle, UserPlus } from 'lucide-react'
import type { ReferralManagementProps } from '@/features/enrollment'

export default function ReferralManagement({ 
  enrollmentData, 
  updateEnrollmentData, 
  referralEmails, 
  newReferral, 
  setNewReferral, 
  addReferral, 
  removeReferral, 
  createUserAccountsForReferrals, 
  creatingAccounts, 
  accountsCreated 
}: ReferralManagementProps) {
  return (
    <div className="border border-accent-200 rounded-lg p-4 lg:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4">
        <div>
          <h4 className="font-semibold text-accent-900">Client Referral</h4>
          <p className="text-sm text-accent-600">Does the client want to make a referral?</p>
        </div>
        <div className="flex space-x-3">
          <button
            className={`px-4 lg:px-6 py-2 rounded-lg font-medium transition-colors ${
              enrollmentData.makeReferral
                ? 'bg-green-600 text-white'
                : 'bg-green-100 hover:bg-green-200 text-green-700'
            }`}
            onClick={() => updateEnrollmentData('makeReferral', true)}
          >
            Yes
          </button>
          <button
            className={`px-4 lg:px-6 py-2 rounded-lg font-medium transition-colors ${
              enrollmentData.makeReferral === false
                ? 'bg-red-600 text-white'
                : 'bg-red-100 hover:bg-red-200 text-red-700'
            }`}
            onClick={() => updateEnrollmentData('makeReferral', false)}
          >
            No
          </button>
        </div>
      </div>
      {enrollmentData.makeReferral && (
        <div className="bg-primary-50 border border-primary-200 rounded p-4 space-y-3">
          <label className="block text-sm font-medium text-primary-900">Referral Email Addresses</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary-400 w-4 h-4" />
              <input
                type="email"
                className="input-field pl-10 w-full"
                placeholder="Enter referral email address"
                value={newReferral}
                onChange={(e) => setNewReferral(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addReferral() } }}
              />
            </div>
            <button className="btn-primary flex items-center justify-center gap-2" onClick={addReferral}>
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          {referralEmails && referralEmails.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {referralEmails.map((email) => (
                <span key={email} className="inline-flex items-center gap-2 bg-white border border-primary-200 text-primary-800 px-2 py-1 rounded">
                  {email}
                  <button className="text-primary-500 hover:text-primary-700" onClick={() => removeReferral(email)} title="Remove">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-primary-600">
            You can add up to 10 emails. The system will send self-enrollment invites with your referral credit.
          </p>
          
          {/* Create User Accounts Button */}
          {referralEmails && referralEmails.length > 0 && (
            <div className="mt-4 pt-4 border-t border-primary-200">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-primary-900 mb-1">Create User Accounts</h5>
                  <p className="text-xs text-primary-600">
                    Create login accounts for referrals. They will receive email with login credentials.
                  </p>
                </div>
                <button
                  onClick={createUserAccountsForReferrals}
                  disabled={creatingAccounts}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    creatingAccounts
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : accountsCreated.length > 0
                      ? 'bg-green-100 text-green-700 border border-green-300'
                      : 'bg-primary-600 hover:bg-primary-700 text-white'
                  }`}
                >
                  {creatingAccounts ? (
                    <>
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating...</span>
                    </>
                  ) : accountsCreated.length > 0 ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      <span>Accounts Created ({accountsCreated.length})</span>
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      <span>Send Referral Invites</span>
                    </>
                  )}
                </button>
              </div>
              
              {accountsCreated.length > 0 && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800 font-medium mb-2">
                    ✅ User accounts created successfully:
                  </p>
                  <div className="space-y-1">
                    {accountsCreated.map((email) => (
                      <div key={email} className="flex items-center space-x-2 text-sm text-green-700">
                        <CheckCircle className="w-3 h-3" />
                        <span>{email}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-green-600 mt-2">
                    Login credentials have been sent to their email addresses.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
